import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Hook customizado para gerenciar mensagens rotativas durante o loading da IA
 * @param {boolean} isLoading - Se está carregando
 * @param {string} context - Contexto das mensagens (ex: 'documentation', 'speech')
 * @param {number} interval - Intervalo entre as mensagens em ms (padrão: 2500ms)
 * @returns {string} - Mensagem atual a ser exibida
 */
export const useAiLoadingMessages = (
  isLoading,
  context = "documentation",
  interval = 2500,
) => {
  const { t } = useTranslation();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Mensagens por contexto
  const messageKeysByContext = {
    documentation: [
      "ai_loading.documentation.reading_content",
      "ai_loading.documentation.analyzing_structure",
      "ai_loading.documentation.checking_best_practices",
      "ai_loading.documentation.improving_clarity",
      "ai_loading.documentation.generating_feedback",
    ],
    speech: [
      "ai_loading.speech.processing_audio",
      "ai_loading.speech.transcribing",
      "ai_loading.speech.formatting",
      "ai_loading.speech.finishing",
    ],
  };

  const messageKeys =
    messageKeysByContext[context] || messageKeysByContext.documentation;

  useEffect(() => {
    if (!isLoading) {
      setCurrentMessageIndex(0);
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentMessageIndex(
        (prevIndex) => (prevIndex + 1) % messageKeys.length,
      );
    }, interval);

    return () => clearInterval(intervalId);
  }, [isLoading, interval, messageKeys.length]);

  if (!isLoading) {
    return "";
  }

  return t(messageKeys[currentMessageIndex]);
};

export default useAiLoadingMessages;
